import { useCallback, useState } from "react";

import dayjs from "dayjs";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

import { Search as SearchIcon } from "@mui/icons-material";
import {
  Box,
  Button,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import Grid from "@mui/material/Grid";
import { PageContainer } from "@/features/ui";

import SearchSvg from "../assets/search.svg";
import { useAuth } from "@/features/authentication";
import { UserOutletsSelect } from "@/features/outlets";
import {
  apiOutletClient,
  formatDate,
  formatDateTime,
  formatMoney,
  formatNumber,
} from "@/features/shared";
import { CenteredLoading } from "@/features/ui";
import {
  DateRangePicker,
  SingleInputDateRangeField,
} from "@mui/x-date-pickers-pro";

export const ExpenseReport = () => {
  const { outlet, changeOutlet, user } = useAuth();
  const theme = useTheme();
  const isMdUp = useMediaQuery(theme.breakpoints.up("md"));
  const isLgUp = useMediaQuery(theme.breakpoints.up("lg"));

  const isExactlyMd = isMdUp && !isLgUp;

  const [dateRange, setDateRange] = useState([dayjs(), dayjs()]);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState();

  const generatePDF = useCallback(
    ({ expenses, totalAmount }) => {
      const doc = new jsPDF({ format: "a4" });
      const padding = 16;
      const pageWidth = doc.internal.pageSize.getWidth();
      const sectionWidth = (pageWidth - 2 * padding) / 3;

      const [startDate, endDate] = dateRange;

      // PDF Header
      doc.setFont("Helvetica", "bold");
      doc.setFontSize(20).setTextColor("#364F6B").text("Biaya", padding, 20);
      doc
        .setFontSize(12)
        .text(outlet.name, pageWidth - padding, 20, { align: "right" });

      // PDF Information Section
      doc.setFontSize(10).setTextColor("#000000");
      doc.text("Tanggal Download", padding, 40);
      doc.text("Didownload Oleh", sectionWidth + padding, 40);
      doc.text("Dari Tanggal - Sampai Tanggal", sectionWidth * 2 + padding, 40);

      // User and Date Range Info
      doc.setFontSize(11).setTextColor("#757575");
      doc.text(formatDateTime(dayjs()), padding, 48, {
        maxWidth: sectionWidth - 8,
      });
      doc.text(user.name, sectionWidth + padding, 48, {
        maxWidth: sectionWidth - 8,
      });
      doc.text(
        `${formatDate(startDate)} - ${formatDate(endDate)}`,
        sectionWidth * 2 + padding,
        48,
        { maxWidth: sectionWidth - 8 }
      );

      // Table Section
      autoTable(doc, {
        startY: 60,
        head: [["Keterangan", "Nominal (Rp)"]],
        body: expenses.map((expense) => [
          expense.note ?? "-",
          formatNumber(expense.amount),
        ]),
        styles: {
          cellPadding: 2, // Default cell padding
          fontSize: 10,
        },
        headStyles: { fillColor: "#364F6B", textColor: "#fff" },
        columnStyles: { 0: { halign: "left" }, 1: { halign: "right" } },
        didParseCell: (data) => {
          if (data.column.index === 1 && data.section === "head") {
            data.cell.styles.halign = "right";
          }

          // Apply margin-left for expense items (child rows)
          if (
            data.cell.section === "body" &&
            data.row.index > 0 &&
            !data.cell.raw.startsWith(data.cell.text[0])
          ) {
            data.cell.styles.cellPadding = { left: 10 }; // Adjust the left padding as needed
          }
        },
      });

      // Total Payment
      doc
        .setFontSize(12)
        .setTextColor("#00695C")
        .text("Total Biaya", 120, doc.lastAutoTable.finalY + 10);
      doc
        .setFontSize(14)
        .setTextColor("#364F6B")
        .text(
          formatMoney(totalAmount),
          pageWidth - padding,
          doc.lastAutoTable.finalY + 10,
          { align: "right" }
        );

      // PDF Preview Blob
      const pdfBlob = doc.output("blob");
      const url = URL.createObjectURL(pdfBlob);
      document.getElementById("pdf-preview").src = url;
    },
    [dateRange, outlet, user]
  );

  // Function to fetch data from the API
  const fetchData = useCallback(async () => {
    const [startDate, endDate] = dateRange;
    if (!outlet || !startDate || !endDate) return;

    setData(null);
    setLoading(true);
    try {
      const {
        data: { data },
      } = await apiOutletClient.get("expenses/report", {
        params: {
          startDate: startDate.format("YYYY-MM-DD"),
          endDate: endDate.format("YYYY-MM-DD"),
        },
      });
      setData(data);
      generatePDF(data);
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  }, [dateRange, generatePDF, outlet]);

  return (
    <PageContainer maxWidth="xl" breadcrumbs={[]}>
      <Stack spacing={3}>
        <Grid container spacing={3}>
          <Grid size={{ xs: 5, md: 4, lg: 5 }}>
            <UserOutletsSelect value={outlet} onChange={changeOutlet} />
          </Grid>
          <Grid size={{ xs: 7, md: 6, lg: 5 }}>
            <DateRangePicker
              value={dateRange}
              disableFuture
              slots={{
                field: SingleInputDateRangeField,
              }}
              slotProps={{
                field: {
                  fullWidth: true,
                },
              }}
              format="DD/MMM/YYYY"
              localeText={{ start: "Dari", end: "Sampai" }}
              onChange={(newDateRange) => {
                setDateRange(newDateRange);
              }}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 2, lg: 2 }}>
            <Button
              fullWidth
              variant="contained"
              startIcon={!isExactlyMd && <SearchIcon />}
              onClick={fetchData}
            >
              {isExactlyMd && <SearchIcon />}
              {!isExactlyMd && "Cari"}
            </Button>
          </Grid>
        </Grid>
        {!loading && !data && (
          <Stack
            sx={{ justifyContent: "center", alignItems: "center", pt: 10 }}
            spacing={2}
          >
            <Box component="img" src={SearchSvg} sx={{
              height: "20vh"
            }} />
            <Typography sx={{ fontSize: "1.5rem" }}>
              Lakukan pencarian
            </Typography>
          </Stack>
        )}
        {loading && <CenteredLoading sx={{ height: "80vh" }} />}
        <iframe
          id="pdf-preview"
          hidden={!data}
          style={{ height: "80vh", width: "100%" }}
        />
      </Stack>
    </PageContainer>
  );
};

export default ExpenseReport;
